import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { TopicFilters } from "@/components/content/topics/TopicFilters";
import { TopicList } from "@/components/content/topics/TopicList";
import { ContentTypeDialog } from "@/components/content/topics/ContentTypeDialog";
import { EmptyTopics } from "@/components/content/topics/EmptyTopics";
import { Topic, TopicMetadata, TopicType } from "@/types/content";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Filter, Sparkles, Loader2, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";

// Type for metadata from Supabase
interface SupabaseMetadata {
  type?: string;
  audience?: string;
  [key: string]: any;
}

// Extend the Topic interface locally to include created_at
interface ExtendedTopic extends Topic {
  created_at?: string;
}

const ContentTopicsPage = () => {
  const { id } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [audienceFilter, setAudienceFilter] = useState<string>("all");
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [showNewTopicDialog, setShowNewTopicDialog] = useState(false);
  const [currentTopicId, setCurrentTopicId] = useState<string | null>(null);
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([]);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newTopic, setNewTopic] = useState({ 
    title: '', 
    type: 'how-to',
    audience: 'marketing-managers' 
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  
  // Initialize with empty array to prevent undefined errors
  const [topics, setTopics] = useState<ExtendedTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingTopics, setIsFetchingTopics] = useState(false);
  const [availableAudiences, setAvailableAudiences] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const TOPICS_PER_PAGE = pageSize;

  // Use the useQuery hook with proper error handling
  const { data: topicsData = [], refetch } = useQuery({
    queryKey: ['content-topics', id],
    queryFn: async () => {
      try {
        // Query the generated_content table with platform 'topics'
        const { data, error } = await supabase
          .from('generated_content')
          .select('*')
          .eq('project_id', id)
          .eq('platform', 'topics')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching topics:', error);
          throw error;
        }
        
        // Transform the data to match the ExtendedTopic interface
        const transformedData = (data || []).map(item => {
          // Safely cast metadata to our expected format
          const metadata = item.metadata as SupabaseMetadata || {};
          
          // Get type and audience data safely
          const type = (metadata.type as TopicType) || 'educational';
          const audience = metadata.audience as string || 'general';
          
          return {
            id: item.id,
            title: item.content || item.title || 'Untitled Topic',
            type,
            audience,
            isFavorite: item.is_favorite || false,
            metadata: metadata as unknown as TopicMetadata,
            created_at: item.created_at
          } as ExtendedTopic;
        });
        
        // Update the local state
        setTopics(transformedData);
        setIsLoading(false);
        
        // Extract and set available audiences for filtering
        const audiences = new Set<string>();
        transformedData.forEach(topic => {
          if (topic.audience) {
            audiences.add(topic.audience);
          }
        });
        setAvailableAudiences(audiences);
        
        return transformedData;
      } catch (error) {
        console.error('Error loading topics:', error);
        setIsLoading(false);
        setTopics([]);
        return [];
      }
    },
    enabled: !!id
  });

  // Handle post-query effects
  useEffect(() => {
    if (topicsData && topicsData.length > 0) {
      setTopics(topicsData);
    }
  }, [topicsData]);

  // Reset audience filter if the selected audience is no longer available
  useEffect(() => {
    if (audienceFilter !== 'all' && !availableAudiences.has(audienceFilter)) {
      setAudienceFilter('all');
    }
  }, [availableAudiences, audienceFilter]);

  const generateTopics = async () => {
    if (!user?.id || !id) {
      toast({
        title: "Error",
        description: "Authentication required",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setIsFetchingTopics(true);

    try {
      // First, fetch the latest analysis results for the project
      const { data: analysisData, error: analysisError } = await supabase
        .from('website_crawls')
        .select('analysis_results')
        .eq('project_id', id)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (analysisError) throw analysisError;

      if (!analysisData?.analysis_results) {
        toast({
          title: "Error",
          description: "No analysis results found. Please analyze your website first.",
          variant: "destructive",
        });
        return;
      }

      // Call the generate-topics edge function
      const { data: topicsData, error: topicsError } = await supabase.functions.invoke('generate-topics', {
        body: { 
          projectId: id,
          analysisResults: analysisData.analysis_results
        }
      });

      if (topicsError) throw topicsError;

      // Safety check for the expected response format
      if (!topicsData || !Array.isArray(topicsData.topics)) {
        throw new Error("Invalid response format from topic generation service");
      }

      // Save the generated topics to the database
      if (topicsData.topics.length > 0) {
        const { error: saveError } = await supabase.from('generated_content').insert(
          topicsData.topics.map((topic: any) => ({
            project_id: id,
            user_id: user.id,
            platform: 'topics',
            content: topic.title,
            metadata: {
              type: topic.type,
              audience: topic.audience
            }
          }))
        );

        if (saveError) throw saveError;
      } else {
        throw new Error("No topics were generated");
      }

      // Refetch topics to update the UI
      await refetch();

      toast({
        title: "Success",
        description: "Topics generated successfully",
      });

    } catch (error: any) {
      console.error('Error generating topics:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate topics",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setIsFetchingTopics(false);
    }
  };

  const handleGenerateContent = (topicId: string) => {
    setCurrentTopicId(topicId);
    setSelectedContentTypes([]);
    setShowContentDialog(true);
  };

  const generateContent = async () => {
    if (!currentTopicId || selectedContentTypes.length === 0) {
      toast({
        title: "Selection required",
        description: "Please select a topic and at least one content type",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingContent(true);
    // Close dialog immediately to show loading state
    setShowContentDialog(false);

    try {
      // Get the selected topic data
      const selectedTopicData = topicsData.find(t => t.id === currentTopicId);
      if (!selectedTopicData) {
        throw new Error("Selected topic not found");
      }

      // Navigate to generated content page with generating flag
      // Use replace instead of navigate to ensure proper history management
      navigate(`/projects/${id}/content?generating=true`, { replace: true });

      // Process all content types in parallel in the background
      // We're not awaiting this since the user has already been redirected
      const generatePromises = selectedContentTypes.map(contentType => {
        const metadata = {
          userId: user.id, // Make sure userId is included for the edge function
          topicId: selectedTopicData.id,
          topic: selectedTopicData.title,
          type: selectedTopicData.type,
          audience: selectedTopicData.audience,
        };

        return supabase.functions.invoke('generate-content', {
          body: { contentType, metadata, projectId: id }
        }).catch(error => {
          console.error(`Error generating ${contentType} content:`, error);
          // Errors are handled on the GeneratedContentPage through the content status
          return null;
        });
      });
      
      // Start the generation but don't wait for it
      Promise.all(generatePromises);

    } catch (error: any) {
      console.error('Error setting up content generation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to set up content generation",
        variant: "destructive",
      });
      
      setIsGeneratingContent(false);
    }
  };

  const handleTopicSelect = (topicId: string) => {
    setSelectedTopics(prev => 
      prev.includes(topicId) 
        ? prev.filter(id => id !== topicId) 
        : [...prev, topicId]
    );
  };

  const handleContentTypeSelect = (type: string) => {
    setSelectedContentTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type) 
        : [...prev, type]
    );
  };

  const toggleFavorite = async (topicId: string) => {
    try {
      const topic = topicsData.find(t => t.id === topicId);
      if (!topic) return;

      const { error } = await supabase
        .from('generated_content')
        .update({ is_favorite: !topic.isFavorite })
        .eq('id', topicId);

      if (error) throw error;

      // Reset to first page when adding a favorite (if not already on first page)
      // This helps users see the newly favorited item at the top
      if (!topic.isFavorite && currentPage !== 1) {
        setCurrentPage(1);
      }

      await refetch();

      toast({
        title: topic.isFavorite ? "Removed from favorites" : "Added to favorites",
        description: `Topic has been ${topic.isFavorite ? 'removed from' : 'added to'} favorites`,
      });
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Error",
        description: "Failed to update favorite status",
        variant: "destructive",
      });
    }
  };

  const deleteTopic = async (topicId: string) => {
    try {
      const { error } = await supabase
        .from('generated_content')
        .delete()
        .eq('id', topicId);

      if (error) throw error;

      await refetch();

      toast({
        title: "Deleted",
        description: "Topic has been deleted",
      });
    } catch (error: any) {
      console.error('Error deleting topic:', error);
      toast({
        title: "Error",
        description: "Failed to delete topic",
        variant: "destructive",
      });
    }
  };

  const handleCreateTopic = async () => {
    setShowNewTopicDialog(true);
  };

  const submitNewTopic = async () => {
    if (!user?.id || !id) {
      toast({
        title: "Error",
        description: "Authentication required",
        variant: "destructive",
      });
      return;
    }

    if (!newTopic.title.trim()) {
      toast({
        title: "Error",
        description: "Topic title is required",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);

    try {
      const { data, error } = await supabase.from('generated_content').insert({
        project_id: id,
        user_id: user.id,
        platform: 'topics',
        content: newTopic.title,
        metadata: {
          type: newTopic.type,
          audience: newTopic.audience
        }
      }).select('*').single();

      if (error) throw error;

      // Refetch topics after creating a new one
      await refetch();

      setNewTopic({ 
        title: '', 
        type: 'how-to',
        audience: 'marketing-managers' 
      });
      
      setShowNewTopicDialog(false);

      toast({
        title: "Success",
        description: "Topic created successfully",
      });
    } catch (error: any) {
      console.error('Error creating topic:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create topic",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateMoreTopics = async () => {
    setIsGeneratingMore(true);
    try {
      const { data: analysisData, error: analysisError } = await supabase
        .from('website_crawls')
        .select('analysis_results')
        .eq('project_id', id)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (analysisError) throw analysisError;

      if (!analysisData?.analysis_results) {
        toast({
          title: "Error",
          description: "No analysis results found. Please analyze your website first.",
          variant: "destructive",
        });
        return;
      }

      const { data: topicsData, error: topicsError } = await supabase.functions.invoke('generate-topics', {
        body: { 
          projectId: id,
          analysisResults: analysisData.analysis_results
        }
      });

      if (topicsError) throw topicsError;

      // Safety check for the expected response format
      if (!topicsData || !Array.isArray(topicsData.topics)) {
        throw new Error("Invalid response format from topic generation service");
      }

      // Save the generated topics to the database
      if (topicsData.topics.length > 0) {
        const { error: saveError } = await supabase.from('generated_content').insert(
          topicsData.topics.map((topic: any) => ({
            project_id: id,
            user_id: user.id,
            platform: 'topics',
            content: topic.title,
            metadata: {
              type: topic.type,
              audience: topic.audience
            }
          }))
        );

        if (saveError) throw saveError;
      } else {
        throw new Error("No topics were generated");
      }

      // Refetch after saving the topics
      await refetch();

      toast({
        title: "Success",
        description: `${topicsData.topics.length} additional topics have been generated`,
      });
    } catch (error: any) {
      console.error('Error generating more topics:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate more topics",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingMore(false);
    }
  };

  // Filter topics based on search query and filters
  const filteredTopics = topicsData.filter(topic => {
    const matchesSearch = !searchQuery || 
      topic.title.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = typeFilter === 'all' || 
      topic.type === typeFilter;
    
    const matchesAudience = audienceFilter === 'all' || 
      topic.audience === audienceFilter;
    
    return matchesSearch && matchesType && matchesAudience;
  });

  // Sort filtered topics with favorites at the top, then by creation date
  const sortedFilteredTopics = [...filteredTopics].sort((a, b) => {
    // First sort by favorite status (handle undefined values safely)
    const aIsFavorite = !!a.isFavorite;
    const bIsFavorite = !!b.isFavorite;
    
    if (aIsFavorite !== bIsFavorite) {
      return aIsFavorite ? -1 : 1;
    }
    
    // If favorite status is the same, maintain the original sort order from the database
    // which is already sorted by created_at in descending order
    return 0;
  });

  // Paginate filtered topics
  const totalPages = Math.ceil(sortedFilteredTopics.length / TOPICS_PER_PAGE);
  const paginatedTopics = sortedFilteredTopics.slice(
    (currentPage - 1) * TOPICS_PER_PAGE, 
    currentPage * TOPICS_PER_PAGE
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, audienceFilter]);

  const currentTopic = topicsData.find(topic => topic.id === currentTopicId);

  return (
    <div className="container py-6 max-w-7xl">
      <div className="flex flex-col space-y-6">
        {/* Header with title and action buttons */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight gradient-text">
              Content Topics
            </h1>
            <p className="text-muted-foreground mt-1">
              Create and manage engaging content topics for your audience
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCreateTopic}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              New Topic
            </Button>
          </div>
        </div>

        {/* Main Content Area */}
        <div>
          {/* Filters */}
          <TopicFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            audienceFilter={audienceFilter}
            setAudienceFilter={setAudienceFilter}
            availableAudiences={Array.from(availableAudiences)}
          />
          
          {/* Content */}
          <div className="mt-4">
            {isLoading ? (
              <Card className="border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
                <CardContent className="p-4 flex items-center justify-center">
                  <div className="flex items-center space-x-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">Loading topics...</p>
                  </div>
                </CardContent>
              </Card>
            ) : paginatedTopics.length > 0 ? (
              <TopicList
                topics={paginatedTopics}
                selectedTopics={selectedTopics}
                onTopicSelect={handleTopicSelect}
                onToggleFavorite={toggleFavorite}
                onDeleteTopic={deleteTopic}
                onGenerateContent={handleGenerateContent}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onCreateTopic={handleCreateTopic}
                isLoading={isLoading}
              />
            ) : (
              <EmptyTopics onGenerate={generateTopics} isGenerating={isGenerating} />
            )}
          </div>
          
          {/* Pagination Controls - Only show if we have more than one page */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center text-sm">
                <span className="text-zinc-700 dark:text-zinc-300">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              <div className="ml-4 flex items-center gap-2">
                <span className="text-sm text-zinc-500 dark:text-zinc-400">Show</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value, 10));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px] text-xs">
                    <SelectValue placeholder="30" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">per page</span>
              </div>
            </div>
          )}

          {/* Generate More Topics Button - Only show if there are already topics */}
          {sortedFilteredTopics.length > 0 && !isLoading && (
            <div className="mt-8 flex justify-center">
              <Button
                variant="outline"
                onClick={handleGenerateMoreTopics}
                disabled={isGeneratingMore}
                className="group border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white hover:border-zinc-300 dark:hover:border-zinc-600 transition-all"
              >
                {isGeneratingMore ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2 text-blue-500 dark:text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300" />
                )}
                Generate More Topics
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* New Topic Dialog */}
      <Dialog open={showNewTopicDialog} onOpenChange={setShowNewTopicDialog}>
        <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden bg-white dark:bg-zinc-800 rounded-xl">
          <div className="bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-900 p-6">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-zinc-900 dark:text-white">
                Create New Topic
              </DialogTitle>
              <DialogDescription className="text-zinc-500 dark:text-zinc-400 mt-1">
                Add a custom topic to generate content for your audience
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Topic Title</label>
              <Textarea
                placeholder="Enter a descriptive title for your topic..."
                value={newTopic.title}
                onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                className="resize-none h-24 bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus-visible:ring-blue-500 dark:text-white"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Content Type</label>
                <Select 
                  value={newTopic.type} 
                  onValueChange={(value) => setNewTopic({ ...newTopic, type: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 dark:text-white">
                    <SelectValue placeholder="Select content type" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-zinc-800 dark:border-zinc-700">
                    <SelectItem value="how-to" className="dark:text-white">How-To Guide</SelectItem>
                    <SelectItem value="listicle" className="dark:text-white">Listicle</SelectItem>
                    <SelectItem value="tutorial" className="dark:text-white">Tutorial</SelectItem>
                    <SelectItem value="faq" className="dark:text-white">FAQ</SelectItem>
                    <SelectItem value="educational" className="dark:text-white">Educational</SelectItem>
                    <SelectItem value="checklist" className="dark:text-white">Checklist</SelectItem>
                    <SelectItem value="comparison" className="dark:text-white">Comparison</SelectItem>
                    <SelectItem value="trend" className="dark:text-white">Trend Analysis</SelectItem>
                    <SelectItem value="data" className="dark:text-white">Data Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Target Audience</label>
                <Select 
                  value={newTopic.audience} 
                  onValueChange={(value) => setNewTopic({ ...newTopic, audience: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 dark:text-white">
                    <SelectValue placeholder="Select target audience" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-zinc-800 dark:border-zinc-700">
                    <SelectItem value="technical-developers" className="dark:text-white">Technical Developers</SelectItem>
                    <SelectItem value="marketing-managers" className="dark:text-white">Marketing Managers</SelectItem>
                    <SelectItem value="startup-founders" className="dark:text-white">Startup Founders</SelectItem>
                    <SelectItem value="product-managers" className="dark:text-white">Product Managers</SelectItem>
                    <SelectItem value="business-owners" className="dark:text-white">Business Owners</SelectItem>
                    <SelectItem value="data-scientists" className="dark:text-white">Data Scientists</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="bg-zinc-50 dark:bg-zinc-900 p-6 flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setShowNewTopicDialog(false)}
              className="w-full sm:w-auto border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={submitNewTopic}
              disabled={!newTopic.title || isCreating}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground transition-all"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Topic
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Type Dialog */}
      <ContentTypeDialog
        open={showContentDialog}
        onOpenChange={setShowContentDialog}
        selectedContentTypes={selectedContentTypes}
        onContentTypeSelect={handleContentTypeSelect}
        onGenerate={generateContent}
        isGenerating={isGeneratingContent}
        topicTitle={currentTopic?.title || ""}
      />
    </div>
  );
};

export default ContentTopicsPage;
